import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type AlertTimedType = 'info' | 'warning' | 'error' | 'success' | 'neutral' | 'closed';

/**
 * Variante do alert-card com barra de progresso e auto-dismiss por tempo.
 * Componente totalmente independente (template/estilos/tipos próprios).
 * O timer pausa no hover e retoma ao sair.
 */
@Component({
  selector: 'app-alert-card-timed',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatProgressBarModule],
  templateUrl: './alert-card-timed.component.html',
  styleUrls: ['./alert-card-timed.component.css']
})
export class AlertCardTimedComponent implements OnInit, OnDestroy {

  /** Tipo do alerta: define cores e ícone padrão */
  @Input() type: AlertTimedType = 'info';

  /** Título opcional (negrito no topo) */
  @Input() title: string = '';

  /** Ícone customizado (se não passar, usa o padrão do tipo) */
  @Input() icon: string = '';

  /** Se true, exibe o botão de fechar (X) */
  @Input() closable: boolean = true;

  /** Tempo em ms para fechar automaticamente. Se 0 ou null, não fecha sozinho. */
  @Input() timerDuration: number = 5000;

  /** Evento emitido ao fechar (manualmente ou pelo timer) */
  @Output() close = new EventEmitter<void>();

  // Controle interno do Timer
  public progressValue: number = 100;
  private timerSubscription?: Subscription;
  private startTime: number = 0;
  private remainingTime: number = 0;
  private readonly INTERVAL_MS = 50;

  ngOnInit(): void {
    if (this.timerDuration > 0) {
      this.startTimer(this.timerDuration);
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  public get defaultIcon(): string {
    if (this.icon) return this.icon;
    switch (this.type) {
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'success': return 'check_circle';
      case 'info': default: return 'info';
    }
  }

  public onClose(): void {
    this.stopTimer();
    this.type = 'closed';
    this.close.emit();
  }

  // --- Lógica do Timer (Pausar no Hover) ---

  public onMouseEnter(): void {
    if (this.timerDuration > 0) {
      this.pauseTimer();
    }
  }

  public onMouseLeave(): void {
    if (this.timerDuration > 0 && this.remainingTime > 0) {
      this.resumeTimer();
    }
  }

  private startTimer(duration: number): void {
    this.startTime = Date.now();
    this.remainingTime = duration;
    this.progressValue = 100;

    this.timerSubscription = timer(0, this.INTERVAL_MS)
      .pipe(takeUntil(timer(duration + this.INTERVAL_MS)))
      .subscribe({
        next: () => {
          const elapsed = Date.now() - this.startTime;
          const currentRemaining = this.remainingTime - elapsed;
          this.progressValue = Math.max(0, (currentRemaining / this.timerDuration) * 100);

          if (currentRemaining <= 0) {
            this.onClose();
          }
        }
      });
  }

  private pauseTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      const elapsed = Date.now() - this.startTime;
      this.remainingTime -= elapsed;
    }
  }

  private resumeTimer(): void {
    this.startTimer(this.remainingTime);
  }

  private stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }
}
